import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { AudioListItem } from '@/design-system/atoms/AudioListItem';
import { LinkListItem } from '@/design-system/atoms/LinkListItem';
import { useBreakpoint } from '@/hooks/useBreakpoint';

import styles from './LinkList.module.scss';
/**
 * A sub-component to display the currently set location.
 * @param {array} columnOne - The first items column
 * @param {array} columnTwo - The second items column
 * @param {string} title - The list title
 * @param {string} listType - The list type: manual, dynamic, audio
 * @param {string} analytics - the analytics object
 * @param {boolean} reserveHeight - whether to pre-reserve height to prevent CLS
 * @returns {React.Component}
 */
const LinkListColumns = ({ columnOne, columnTwo, title, listType, analytics, reserveHeight }) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.medium;

    // get the approx list height to prevent CLS issues
    const getListHeight = () => {
        const itemBaseHeight = 58;
        const listVerticalPadding = 56;
        const totalColumns = columnOne?.length + columnTwo?.length;

        // if mobile, the multiplier is the total items; desktos relies on the first column items only
        const multiplier = isMobile ? totalColumns : columnOne?.length;
        const minHeight = itemBaseHeight * multiplier;

        return minHeight + listVerticalPadding;
    };

    return (
        <div
            style={reserveHeight && getListHeight() ? { minHeight: `${getListHeight()}px` } : {}}
            role="list"
            className={styles[`link-list__list`]}
        >
            <span role="presentation" className={styles[`link-list__list-column`]}>
                {columnOne?.map(({ ...item }, index) => {
                    return renderListItems(item, title, listType, index, analytics);
                })}
            </span>
            <span
                role="presentation"
                className={classNames(styles[`link-list__list-column`], 'axe-jest-ignore')}
            >
                {columnTwo?.map(({ ...item }, index) => {
                    return renderListItems(item, title, listType, index, analytics);
                })}
            </span>
        </div>
    );
};

// renders the individual list items
const renderListItems = (item, title, listType, index, analytics) => {
    if (listType === 'audio') {
        return <AudioListItem label={item.label} src={item.href} {...item} />;
    } else {
        return (
            <LinkListItem
                listType
                key={index}
                {...item}
                linkWrapper="li"
                description={item.itemDescription}
                analytics={{
                    componentTitle: title,
                    ...analytics,
                }}
            />
        );
    }
};

LinkListColumns.propTypes = {
    columnOne: PropTypes.array.isRequired,
    columnTwo: PropTypes.array.isRequired,
    /**
     * Title of the Link List
     */
    title: PropTypes.string.isRequired,
    listType: PropTypes.string.isRequired,
    reserveHeight: PropTypes.bool,
};

export default LinkListColumns;
